import { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Refresh as RefreshIcon, CalendarMonth, ArrowBack, Home } from '@mui/icons-material';
import OCIBadge from '../components/oci/OCIBadge';
import OCIFactorsList from '../components/oci/OCIFactorsList';
import { ociService } from '../services/ociService';

export default function OCIDashboardPage() {
  const { pageId } = useParams<{ pageId: string }>();
  const [searchParams] = useSearchParams();
  const pageUrl = searchParams.get('pageUrl');
  const navigate = useNavigate();
  const [engine, setEngine] = useState<'google' | 'bing'>('google');
  const [window, setWindow] = useState<28 | 90>(28);

  // Use pageId if available (from path), otherwise use pageUrl (from query param)
  const lookupKey = pageId ? `pageId-${pageId}` : `pageUrl-${pageUrl}`;
  const hasValidLookup = !!(pageId || pageUrl);

  if (!hasValidLookup) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Page ID or Page URL is required</Alert>
      </Container>
    );
  }

  const numericPageId = pageId ? parseInt(pageId, 10) : undefined;

  // Fetch OCI score
  const ociQuery = useQuery({
    queryKey: ['oci', lookupKey, engine, window],
    queryFn: () => {
      if (pageId && numericPageId) {
        return ociService.getOCIScore(numericPageId, engine, window);
      } else if (pageUrl) {
        return ociService.getOCIScoreByUrl(pageUrl, engine, window);
      }
      throw new Error('No valid page identifier');
    },
    refetchInterval: 60000, // Refresh every 60s
    enabled: hasValidLookup,
  });

  // Fetch OCI history
  const historyQuery = useQuery({
    queryKey: ['ociHistory', lookupKey, engine, window],
    queryFn: () => {
      if (pageId && numericPageId) {
        return ociService.getOCIHistory(numericPageId, engine, 30);
      } else if (pageUrl) {
        return ociService.getOCIHistoryByUrl(pageUrl, engine, window);
      }
      throw new Error('No valid page identifier');
    },
    enabled: hasValidLookup,
  });

  // Recompute mutation
  const recomputeMutation = useMutation({
    mutationFn: () => {
      if (numericPageId) {
        return ociService.recomputeOCI(numericPageId, engine, window);
      }
      throw new Error('Recompute only supported for pageId-based lookup');
    },
    onSuccess: () => {
      ociQuery.refetch();
      historyQuery.refetch();
    },
  });

  const ociData = ociQuery.data;
  const insufficientData = ociData?.minImpressionsRequired && 
    ociData?.actualImpressions && 
    ociData.actualImpressions < ociData.minImpressionsRequired;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Breadcrumb Navigation */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/dashboard')}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
          >
            <Home sx={{ fontSize: 18 }} />
            Dashboard
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/pages')}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
          >
            Pages
          </Link>
          <Typography color="text.primary" variant="body2">OCI Analysis</Typography>
        </Breadcrumbs>
      </Box>

      {/* Back Button */}
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/pages')}
          variant="outlined"
          size="small"
        >
          Back to Pages List
        </Button>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Observed Competitiveness Index (OCI)
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Page #{pageId} - Investment Priority Analysis
        </Typography>
      </Box>

      {/* Controls */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Engine Selector */}
          <ToggleButtonGroup
            value={engine}
            exclusive
            onChange={(_, value) => value && setEngine(value)}
            size="small"
          >
            <ToggleButton value="google">
              Google
            </ToggleButton>
            <ToggleButton value="bing">
              Bing
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Window Selector */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Time Window</InputLabel>
            <Select
              value={window}
              label="Time Window"
              onChange={(e) => setWindow(e.target.value as 28 | 90)}
            >
              <MenuItem value={28}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarMonth sx={{ fontSize: 18 }} />
                  28 days
                </Box>
              </MenuItem>
              <MenuItem value={90}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarMonth sx={{ fontSize: 18 }} />
                  90 days
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ flex: 1 }} />

          {/* Recompute Button */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => recomputeMutation.mutate()}
            disabled={recomputeMutation.isPending || ociQuery.isLoading}
          >
            Recompute
          </Button>
        </Box>
      </Paper>

      {ociQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : ociQuery.isError ? (
        <Alert severity="error">Failed to load OCI data</Alert>
      ) : ociData ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Insufficient Data Warning */}
          {insufficientData && (
            <Alert severity="warning">
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Insufficient Data for Reliable Analysis
              </Typography>
              <Typography variant="caption">
                Current impressions: {ociData.actualImpressions?.toLocaleString()} | 
                Required: {ociData.minImpressionsRequired?.toLocaleString()}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Consider increasing the time window to 90 days or waiting for more data to accumulate.
              </Typography>
            </Alert>
          )}

          {/* OCI Score Card */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Competitiveness Score
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Computed {new Date(ociData.computedAt).toLocaleString()}
                </Typography>
              </Box>
              <OCIBadge score={ociData.score} level={ociData.level} size="medium" showScore />
            </Box>

            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {ociData.level === 'LOW' && '✓ Good opportunity for investment - low competition detected'}
                {ociData.level === 'MEDIUM' && '⚠ Moderate competition - focused effort recommended'}
                {ociData.level === 'HIGH' && '⚠ High competition - substantial resources required'}
                {ociData.level === 'VERY_HIGH' && '✗ Extremely competitive - consider alternative strategies'}
              </Typography>
            </Box>
          </Paper>

          {/* Factors List */}
          {ociData.factors && ociData.factors.length > 0 && (
            <OCIFactorsList factors={ociData.factors} expanded={false} />
          )}

          {/* History Summary */}
          {historyQuery.data && historyQuery.data.length > 0 && (
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 2 }}>
                Recent Trend
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {historyQuery.data.slice(0, 7).reverse().map((item, index) => (
                  <Chip
                    key={index}
                    label={`${item.score}`}
                    size="small"
                    variant="outlined"
                    color={
                      item.level === 'LOW' ? 'success' :
                      item.level === 'MEDIUM' ? 'info' :
                      item.level === 'HIGH' ? 'warning' : 'error'
                    }
                  />
                ))}
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                Last 7 computations (oldest to newest)
              </Typography>
            </Paper>
          )}
        </Box>
      ) : null}
    </Container>
  );
}
